/**
 * Feature flag administration (`app/services/feature_flag_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { assertPojo } from '@brainfocus/web/api/response';

// ----- Feature flag administration (super-admin only) -----

export async function getFeatureFlagRegistry(): Promise<FeatureFlagDefinition[]> {
    const response = await client.request('GET', '/api/admin/feature-flags/registry');
    assertPojo<{ flags: FeatureFlagDefinition[] }>(response);
    return response.flags;
}

export async function getFeatureFlagOverview(): Promise<Record<string, { user: number; institution: number }>> {
    const response = await client.request('GET', '/api/admin/feature-flags/overview');
    assertPojo<{ overview: Record<string, { user: number; institution: number }> }>(response);
    return response.overview;
}

export async function getFeatureFlagOverrides(flagKey: string): Promise<FeatureFlagOverridesByFlag> {
    const response = await client.request('GET', `/api/admin/feature-flags/flag/${encodeURIComponent(flagKey)}`);
    assertPojo<FeatureFlagOverridesByFlag>(response);
    return response;
}

export async function getUserFeatureFlags(userId: number): Promise<{
    user: { id: number; email: string };
    flags: UserFeatureFlagRow[];
}> {
    const response = await client.request('GET', `/api/admin/feature-flags/user/${userId}`);
    assertPojo<{
        user: { id: number; email: string };
        flags: UserFeatureFlagRow[];
    }>(response);
    return response;
}

export async function getInstitutionFeatureFlags(institutionId: number): Promise<{
    institution: { id: number; name: string };
    flags: InstitutionFeatureFlagRow[];
}> {
    const response = await client.request('GET', `/api/admin/feature-flags/institution/${institutionId}`);
    assertPojo<{
        institution: { id: number; name: string };
        flags: InstitutionFeatureFlagRow[];
    }>(response);
    return response;
}

export async function setUserFeatureFlag(userId: number, flagKey: string, enabled: boolean): Promise<void> {
    await client.request('PUT', `/api/admin/feature-flags/user/${userId}/${encodeURIComponent(flagKey)}`, { enabled });
}

export async function clearUserFeatureFlag(userId: number, flagKey: string): Promise<void> {
    await client.request('DELETE', `/api/admin/feature-flags/user/${userId}/${encodeURIComponent(flagKey)}`);
}

export async function setInstitutionFeatureFlag(institutionId: number, flagKey: string, enabled: boolean): Promise<{ affectedUserOverrides: number }> {
    const response = await client.request('PUT', `/api/admin/feature-flags/institution/${institutionId}/${encodeURIComponent(flagKey)}`, { enabled });
    assertPojo<{ affectedUserOverrides: number }>(response);
    return { affectedUserOverrides: response.affectedUserOverrides };
}

export async function clearInstitutionFeatureFlag(institutionId: number, flagKey: string): Promise<void> {
    await client.request('DELETE', `/api/admin/feature-flags/institution/${institutionId}/${encodeURIComponent(flagKey)}`);
}

export interface FeatureFlagDefinition {
    key: string;
    description: string;
    default: boolean;
    sensitive: boolean;
}

export interface FeatureFlagOverridesByFlag {
    users: Array<{
        id: number;
        userId: number;
        userEmail: string | null;
        flagKey: string;
        enabled: boolean;
        updatedAt: string | null;
        updatedByUserId: number | null;
    }>;
    institutions: Array<{
        id: number;
        institutionId: number;
        institutionName: string | null;
        flagKey: string;
        enabled: boolean;
        updatedAt: string | null;
        updatedByUserId: number | null;
    }>;
}

export interface UserFeatureFlagRow {
    key: string;
    description: string;
    default: boolean;
    sensitive: boolean;
    effectiveValue: boolean;
    source: 'user' | 'institution' | 'default';
    userOverride: boolean | null;
}

export interface InstitutionFeatureFlagRow {
    key: string;
    description: string;
    default: boolean;
    sensitive: boolean;
    effectiveValue: boolean;
    source: 'user' | 'institution' | 'default';
    institutionOverride: boolean | null;
}
